import { useNavigate } from "react-router-dom";
import { Heart, Users, Calendar, CheckCircle } from "lucide-react";
import { Helmet } from "react-helmet";

export default function Booking() {
  const navigate = useNavigate();

  const coaching = [
    {
      icon: <Heart size={40} />,
      title: "Couples Coaching",
      text: "Strengthen communication, rebuild trust, and grow together through biblical guidance.",
    },
    {
      icon: <Users size={40} />,
      title: "Premarital Coaching",
      text: "Build a healthy, Christ-centered foundation before saying 'I Do.'",
    },
    {
      icon: <CheckCircle size={40} />,
      title: "Individual Coaching",
      text: "Find clarity, healing, and personal growth for every season of life.",
    },
  ];

  const steps = [
    "Book Your Session",
    "Choose Your Date",
    "Meet With Maurice & Kim",
    "Grow Together",
  ];

  return (
    <main className="bg-[#FFFDF9]">
      <Helmet>
        <title>
          Book Relationship Coaching | Pure Business Solutions
        </title>

        <meta
          name="description"
          content="Book a relationship coaching session with Maurice and Kim Johnson. Faith-based coaching for couples, premarital counseling, marriage enrichment, and personal growth."
        />

        <meta
          name="keywords"
          content="book relationship coaching, marriage coaching, couples coaching, premarital coaching, Christian relationship coaching, biblical marriage coaching, relationship counselor, Pure Business Solutions"
        />

        <meta name="author" content="Pure Business Solutions" />

        <link
          rel="canonical"
          href="https://www.pbizs.com/booking"
        />

        {/* Open Graph */}
        <meta
          property="og:title"
          content="Book Relationship Coaching | Pure Business Solutions"
        />

        <meta
          property="og:description"
          content="Schedule a coaching session with Maurice and Kim Johnson to strengthen your marriage, relationships, communication, and personal growth."
        />

        <meta
          property="og:image"
          content="https://www.pbizs.com/logo512.png"
        />

        <meta
          property="og:url"
          content="https://www.pbizs.com/booking"
        />

        <meta property="og:type" content="website" />

        {/* Twitter */}
        <meta
          name="twitter:card"
          content="summary_large_image"
        />

        <meta
          name="twitter:title"
          content="Book Relationship Coaching | Pure Business Solutions"
        />

        <meta
          name="twitter:description"
          content="Book a faith-based relationship coaching session with Maurice and Kim Johnson."
        />

        <meta
          name="twitter:image"
          content="https://www.pbizs.com/logo512.png"
        />
      </Helmet>
      {/* Hero */}
      <section className="bg-gradient-to-br from-[#EAF6FF] via-white to-[#F8F3ED] py-32 px-6">
        <div className="max-w-5xl mx-auto text-center">
          <p className="uppercase tracking-[5px] text-[#D4AF37] font-semibold">
            Marriage & Relationship Coaching
          </p>

          <h1 className="text-5xl lg:text-7xl font-bold mt-6 text-[#2C2C2C]">
            Ready To Strengthen
            <br />
            Your Relationship?
          </h1>

          <div className="w-28 h-1 bg-[#D4AF37] mx-auto my-8 rounded-full" />

          <p className="max-w-3xl mx-auto text-lg text-gray-600 leading-8">
            Whether you're preparing for marriage, rebuilding after challenges,
            or simply ready to grow closer together, we'd be honored to walk
            alongside you.
          </p>

          <button className="mt-10 bg-[#D4AF37] hover:bg-[#BE9924] text-white px-8 py-4 rounded-full font-semibold transition">
            Book Your Session
          </button>
        </div>
      </section>

      {/* Coaching */}
      <section className="py-24 px-6">
        <div className="max-w-7xl mx-auto text-center">
          <h2 className="text-5xl font-bold text-[#2C2C2C]">
            Coaching Designed
            <br />
            For Every Journey
          </h2>

          <div className="grid md:grid-cols-3 gap-8 mt-16">
            {coaching.map((item, index) => (
              <div
                key={index}
                className="bg-white rounded-[30px] p-10 shadow-lg hover:-translate-y-2 transition"
              >
                <div className="text-[#D4AF37] flex justify-center mb-6">
                  {item.icon}
                </div>

                <h3 className="text-2xl font-bold mb-4">{item.title}</h3>

                <p className="text-gray-600 leading-8">{item.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="bg-[#A8D5FF] py-24 px-6">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-5xl font-bold text-center text-[#2C2C2C]">
            What You'll Gain
          </h2>

          <div className="grid md:grid-cols-2 gap-8 mt-16 max-w-3xl mx-auto">
            <div className="space-y-6 text-lg text-center">
              <p>✔ Better Communication</p>
              <p>✔ Biblical Guidance</p>
              <p>✔ Practical Relationship Tools</p>
              <p>✔ Stronger Faith</p>
            </div>

            <div className="space-y-6 text-lg text-center">
              <p>✔ Conflict Resolution</p>
              <p>✔ Renewed Hope</p>
              <p>✔ Greater Connection</p>
              <p>✔ Lasting Growth</p>
            </div>
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-24 px-6">
        <div className="max-w-5xl mx-auto text-center">
          <Calendar size={50} className="mx-auto text-[#D4AF37]" />

          <h2 className="text-5xl font-bold mt-6 text-[#2C2C2C]">
            Booking Is Easy
          </h2>

          <div className="grid md:grid-cols-4 gap-8 mt-16">
            {steps.map((step, index) => (
              <div key={index}>
                <div className="w-16 h-16 bg-[#D4AF37] text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto">
                  {index + 1}
                </div>

                <p className="mt-5 font-semibold text-blue-600 ">{step}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Quote */}
      <section className="bg-[#F8F3ED] py-24 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl lg:text-5xl italic font-semibold text-[#2C2C2C] leading-relaxed">
            <span className="text-[#D4AF37]">
              Your relationship can be stronger.
            </span>
            <br />
            Your communication can improve.
            <br />
            Your love can deepen.
            <br />
            Your marriage can flourish.
          </h2>
        </div>
      </section>

      {/* CTA */}
      <section className="py-28 px-6">
        <div className="max-w-5xl mx-auto bg-[#EAF6FF] rounded-[40px] p-16 text-center shadow-xl">
          <h2 className="text-5xl font-bold text-[#2C2C2C]">Ready To Begin?</h2>

          <div className="w-24 h-1 bg-[#D4AF37] mx-auto my-8 rounded-full" />

          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-8">
            Helping couples do the heart work that builds lasting love. We'd be
            honored to walk alongside you.
          </p>

          <div className="flex flex-col sm:flex-row justify-center gap-5 mt-10">
            <button className="bg-[#D4AF37] hover:bg-[#BE9924] text-white px-8 py-4 rounded-full font-semibold transition">
              Book Your Coaching Session
            </button>

            <button
              onClick={() => navigate("/retreats")}
              className="border-2 border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-white px-8 py-4 rounded-full font-semibold transition"
            >
              View Upcoming Retreats
            </button>
          </div>
        </div>
      </section>
    </main>
  );
}
